import { catchError, Observable, tap, throwError } from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { EventSellerService } from './event-seller.service';
import { ProductService } from '../product/product.service';
import { CustomToastService } from '../custom-toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Share } from '@capacitor/share';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { canUseNativeShare } from 'src/app/utils/native-share';
import { User } from 'src/app/models/core/user.model';
import { UserProfileApiServiceService } from '../profile/user-profile-api-service.service';

@Injectable({
  providedIn: 'root'
})
export class ShareMicrositeStateService {
  private platform = inject(Platform);

  constructor(
    private eventSeller: EventSellerService,
    private productSrv: ProductService,
    private toast: CustomToastService,
    private translate: TranslateService,
    private userProfileApi: UserProfileApiServiceService,
  ) { }

  private _isNative = signal(Capacitor.isNativePlatform());
  private _isAndroid = signal(this.platform.is('android'));

  readonly isAndroidNative = computed(() => this._isNative() && this._isAndroid());
  readonly isWeb = computed(() => !this._isNative());

  /* ────────────────────────────  Signals externos  ─────────────────────── */
  readonly event = computed(() => this.eventSeller.event());
  readonly isLoadingEvent = computed(() => this.event() === null);

  readonly restrictedProducts = computed(() => this.productSrv.restrictedProducts());
  readonly promotionalProducts = computed(() => this.productSrv.promotionalProducts());
  readonly loadingProducts = computed(() => this.productSrv.loadingMicrositeProducts());

  /* ────────────────────────────  Signals base  ──────────────────────────── */
  private _sellerCode = signal<string | null>(null);
  private _msgIndex = signal<number>(0);
  private _isCustomCode = signal(false);
  private _codeDraft = signal('');
  private _isSavingCode = signal(false);

  readonly sellerCode = computed(() => this._sellerCode());
  /**
   * `sellerCode` como Observable para que los consumidores puedan sembrar un
   * FormControl con `.subscribe()` en vez de `effect()`: los componentes de
   * esta pantalla se testean instanciándolos con `new X(stateStub)`, fuera de
   * un injection context, y `effect()` explota ahí (NG0203).
   */
  readonly sellerCode$ = toObservable(this._sellerCode);
  readonly selectedMsgIndex = computed(() => this._msgIndex());
  readonly isCustomCode = computed(() => this._isCustomCode());
  readonly isSavingCode = computed(() => this._isSavingCode());
  /** El campo de código es editable siempre; esto sólo dice si el borrador difiere de lo guardado. */
  readonly isDirty = computed(() => this._codeDraft() !== (this._sellerCode() ?? ''));

  readonly isNative = signal(Capacitor.isNativePlatform());

  /* ──────────────────────────  Derivados  ───────────────────── */
  readonly micrositeLink = computed(() => {
    const ev = this.event();
    const sc = this._codeDraft();
    return ev ? `${environment.ecommerceDomain}/event/${ev.slug}?code=${sc}` : '';
  });

  /* ─────────────────────────────  Constants  ───────────────────────────── */
  readonly shareMessages: string[] = [
    `🎟️ ¡No te quedes afuera de este eventazo!\nConseguí tus entradas desde mi link y viví una experiencia única 🎉\n[ACA TU LINK]\n📲 ¡Sumate y compartilo con tus amigos!\n#EventoImperdible #ViviloConNosotros 🎧✨`,
    `🚨 ¡Se viene un fiestón y vos tenés que estar ahí!\n🎟️ Sacá tu entrada desde mi link: [ACA TU LINK]\n💃🕺 Nos vemos ahí 🔥`,
    `✨ Ya tengo mi entrada, ¿y vos?\n🎟️ Comprala desde mi link: [ACA TU LINK]`,
    `📅 La fiesta ya tiene fecha… y vos no te la podés perder.\n🎟️ Conseguí tu entrada desde mi link: [ACA TU LINK]\nNos vemos en la pista 💃🕺\n#NocheÉpica #NosVemosAhí ✨`,
    `🎉 ¿Plan para el finde? Ya lo tenés.\n🎶 Fiesta asegurada, buena música y la mejor vibra.\n🎟️ Sacá tu entrada por acá: [ACA TU LINK]\n👯‍♂️ ¡Invitá a tu crew y rompemos la noche! 🌟`,
    `📣 Hay una fiesta que va a dar que hablar…\nY vos podés ser parte.\n🎟️ Entradas solo por este link: [ACA TU LINK]\n🔊 Nos vemos bajo las luces. ✨\n#ModoNoche #PartyTime`,
  ];

  readonly shareMessage = computed(() =>
    this.shareMessages[this._msgIndex()].replace('[ACA TU LINK]', this.micrositeLink())
  );

  /* ─────────────────────────────  Mutators  ────────────────────────────── */
  /** El código llega una sola vez desde /me; sembrar el borrador acá evita que el campo arranque "sucio". */
  setSellerCode(code: string) {
    this._sellerCode.set(code);
    this._codeDraft.set(code);
  }
  setSelectedMsg(index: number) { this._msgIndex.set(index); }
  setCustomCode(isCustom: boolean) { this._isCustomCode.set(isCustom); }

  /* ─────────────────────  Edición del código de microsite  ─────────────── */
  updateCodeDraft(value: string): void {
    this._codeDraft.set(value);
  }

  updateCode(code: string): Observable<User> {
    this._isSavingCode.set(true);
    return this.userProfileApi.updateCode(code).pipe(
      tap(user => {
        this._sellerCode.set(user.code);
        this._codeDraft.set(user.code);
        this._isCustomCode.set(user.custom_code ?? true);
        this._isSavingCode.set(false);
        this.toast.show(this.translate.instant('share-microsite.save-code-success'), 3000, 'success');
      }),
      catchError(error => {
        this._isSavingCode.set(false);
        return throwError(() => error);
      })
    );
  }

  /* ───────────────────────  Initial data loading ───────────────────────── */

  loadAll(eventId: number): void {
    if (!this.event() || this.event()!.id !== eventId) {
      this.eventSeller.loadEvent(eventId);
    }
    this.productSrv.loadMicrositeProducts(eventId);
  }

  copyLink() {
    const link = this.micrositeLink();
    if (!link) return;

    const htmlContent = `
      <p style="color: #000; white-space: pre-wrap;">${link}</p>
    `;

    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    document.body.appendChild(tempElement);

    const range = document.createRange();
    range.selectNode(tempElement);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    const copied = document.execCommand('copy');
    document.body.removeChild(tempElement);

    if (copied) {
      this.toast.show(this.translate.instant('share-microsite.copy-link-success'), 3000, 'success');
    } else {
      this.toast.show(this.translate.instant('share-microsite.copy-link-error'), 3000, 'error');
    }
  }

  async shareLink() {
    const link = this.micrositeLink();
    if (!link) return;

    // "Compartir link" se ve también en web: sin Web Share, copiar es lo más útil.
    if (!canUseNativeShare(this.isNative())) {
      this.copyLink();
      return;
    }

    try {
      await Share.share({
        title: 'Compartir evento',
        text: link,
        dialogTitle: 'Compartir con...'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  }

  get shareMessageToCopy(): string {
    const message = this.shareMessages[this.selectedMsgIndex()];
    const link = this.micrositeLink();
    return message.replace('[ACA TU LINK]', link ?? '');
  }

  copyShareMessageToClipboard(
    successMsg = this.translate.instant('share-ticket-modal.copy-message-success'),
    errorMsg = this.translate.instant('share-ticket-modal.copy-message-error')
  ) {
    navigator.clipboard.writeText(this.shareMessageToCopy).then(() => {
      this.toast.show(successMsg, 3000, 'success');
    }).catch(() => {
      this.toast.show(errorMsg, 3000, 'error');
    });
  }


  async shareMessageNative() {
    // "Compartir mensaje" se ve también en web: sin Web Share, copiar es lo más útil.
    if (!canUseNativeShare(this.isNative())) {
      this.copyShareMessageToClipboard();
      return;
    }

    try {
      await Share.share({
        title: 'Compartir evento',
        text: this.shareMessageToCopy,
        dialogTitle: 'Compartir con...'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  }

}